import React, { useCallback, useEffect, useRef } from 'react';
import MotionPaceChart from './MotionPaceChart';
import {
  MOTION_MODES,
  MOTION_SECTION,
  MOTION_PACE_CONTROLS,
} from '../../lib/operationCopy';
import {
  MOTION_MODE_TRIM,
  MOTION_MODE_PACE,
  motionModeChanges,
} from '../../lib/editDraft';
import {
  MAX_SPEED_MIN,
  MAX_SPEED_MAX,
  MAX_SPEED_STEP,
  SMOOTHNESS_MIN,
  SMOOTHNESS_MAX,
  SMOOTHNESS_STEP,
  SPEED_CURVE_FIELD,
  resolveMaxSpeed,
  resolveSmoothness,
  speedCurveMatchesSettings,
  formatSpeed,
  formatRampSeconds,
} from '../../lib/motionPace';
import { trackEvent } from '../../analytics';
import { diffSettingsForEvents } from '../../analytics-helpers';
import { formatTime } from '../../utils/motionTimeline';

// The Motion section's body (#532) — the segmented MotionTrim | MotionPace
// toggle and the SHARED CHART SLOT beneath it.
//
// ONE SECTION, TWO MODES. MotionPace speeds through still stretches, MotionTrim
// deletes them; the registry declares them an exclusive group, so the toggle is
// that rule made visible rather than a second implementation of it — `mode` is
// resolved by `motionModeOf` (lib/editDraft.js), which runs the same
// `resolveOperations` the render does.
//
// MOTIONPACE IS THE LEFT CELL and MotionTrim the right one, because MotionPace
// is the mode a project with no saved motion choice now opens on while the
// `motionPace` flag is lit (`seedEditDraft`, lib/editDraft.js).
//
// The chart slot is deliberately ONE slot. In trim mode it holds the existing
// kept-segment timeline, unchanged and untouched by this change (it arrives as
// `motionControls`); in pace mode it holds the speed-curve chart. Two stacked
// charts would invite the reader to compare a trim they are not getting against
// a pace they are.

// How long a slider must sit still before it is committed. The commit is a
// server round trip — the speed curve is recomputed there — so this cannot fire
// per pixel of a drag. 500ms matches SettingsEditor's debounce, which is the
// other slider on this page that persists.
const PACE_COMMIT_DEBOUNCE_MS = 500;

const sliderFillStyle = (percent) => ({ '--fill': `${percent}%` });

const percentInRange = (value, min, max) => {
  if (!(max > min)) return 0;
  const p = ((value - min) / (max - min)) * 100;
  if (!Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, p));
};

const ModeOption = ({ mode, selected, onSelect }) => (
  <label
    className={`motion-mode${selected ? ' is-selected' : ''}`}
    data-testid={`motion-mode-${mode.id}`}
  >
    <input
      type="radio"
      name="motion-mode"
      value={mode.id}
      checked={selected}
      onChange={() => onSelect(mode.id)}
      className="motion-mode__radio"
    />
    <span className="motion-mode__dot" aria-hidden="true" />
    <span className="motion-mode__text">
      <span className="motion-mode__name">{mode.name}</span>
      <span className="motion-mode__desc">{mode.description}</span>
    </span>
  </label>
);

const PaceSlider = ({
  id, label, hint, caption, min, max, step, value, minLabel, maxLabel,
  display, onChange,
}) => (
  <div className="motion-pace__control">
    <label className="op-card__field-label" htmlFor={id}>
      {label}
    </label>
    <div className="motion-pace__row">
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        className="setting-input"
        style={sliderFillStyle(percentInRange(value, min, max))}
        aria-label={label}
        aria-describedby={`${id}-caption`}
        title={hint}
      />
      <span className="motion-pace__value" data-testid={`${id}-value`}>{display}</span>
    </div>
    <div className="motion-pace__ends" aria-hidden="true">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
    <p className="motion-pace__caption" id={`${id}-caption`}>{caption}</p>
  </div>
);

// The speed-dial switch (#533). Its own row rather than a third cell in the
// two-column slider grid: it is a different KIND of control — the sliders tune
// how the footage is paced, this decides whether the pacing is annotated on top
// of it — and dropping a switch into a column sized for a track and a value box
// reads as a slider that failed to render.
//
// Built from the same `toggle-switch` markup OperationCards uses for every
// other switch in the editor, so the control the user has already met on
// Deflicker and the watermark behaves identically here.
const PaceToggle = ({ id, label, hint, caption, checked, onChange }) => (
  <div className="motion-pace__switch" data-testid="motion-pace-dial-control">
    <div className="motion-pace__switch-text">
      <label className="op-card__field-label" htmlFor={id} title={hint}>
        {label}
      </label>
      <p className="motion-pace__caption" id={`${id}-caption`}>{caption}</p>
    </div>
    <label className="toggle-switch">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
        aria-describedby={`${id}-caption`}
      />
      <span className="toggle-slider"></span>
    </label>
  </div>
);

const MotionSection = ({
  draft,
  onDraftChange,
  // Persists the motion keys so the server recomputes the speed curve, called
  // only for changes MotionPace is part of — see the call sites.
  //
  // TAKES ONLY THE KEYS THAT CHANGED, never a whole draft. The parent merges
  // them into the draft as it stands at COMMIT time, which is the only moment
  // that is guaranteed current: a debounced slider commit can land from a
  // section that has already unmounted, and one of the unmount paths is the
  // Motion master switch. A draft snapshot taken here would still say MotionPace
  // is on, and flushing it would write that back over the switch's own write.
  onMotionCommit,
  mode,
  video,
  projectId,
  isRecalculating = false,
  // The existing motion tuning UI + kept-segment timeline. Rendered verbatim in
  // trim mode; this component does not touch it.
  motionControls,
  // The editor's <video> (the SOURCE footage) and its URL. Both go straight to
  // the pace chart, which seeks that player on a click and draws a playhead on
  // it — the same thing the trim timeline inside `motionControls` does with the
  // same element, so the two modes of this slot behave alike.
  videoRef,
  videoUrl,
}) => {
  // Per-KEY timers, not one shared one: a shared timer would let a smoothness
  // drag swallow the max-speed drag that came just before it, and the earlier
  // control would never be committed OR reported.
  const timersRef = useRef({});
  // The VALUE each armed timer is holding, so unmount can commit it instead of
  // throwing it away.
  const pendingRef = useRef({});
  const lastEmittedRef = useRef({
    maxSpeed: draft.maxSpeed,
    smoothness: draft.smoothness,
  });

  // Diffed and recorded PER KEY. The two timers can interleave, and a diff over
  // both keys would then report the OTHER control as having moved back to
  // whatever the draft said when this timer was armed — an event for a change
  // nobody made.
  const reportPaceSetting = useCallback((key, value) => {
    diffSettingsForEvents(
      { [key]: lastEmittedRef.current[key] },
      { [key]: value },
      [key],
    ).forEach((evt) => trackEvent('setting_adjusted', {
      ...evt,
      project_id: projectId,
    }));
    lastEmittedRef.current = { ...lastEmittedRef.current, [key]: value };
  }, [projectId]);

  // The unmount cleanup below is armed once and so cannot close over props;
  // this ref keeps it pointed at the current ones.
  const flushRef = useRef(null);
  flushRef.current = () => {
    const pending = pendingRef.current;
    const keys = Object.keys(pending);
    if (!keys.length) return;
    const values = { ...pending };
    keys.forEach((k) => { delete pending[k]; });
    keys.forEach((k) => reportPaceSetting(k, values[k]));
    // ONE write for however many keys were armed. Per-key writes here would be
    // byte-identical payloads, each paying for its own curve recomputation.
    onMotionCommit(values);
  };

  // FLUSH ON UNMOUNT, DON'T DROP. Switching to the Footage tab inside the 500ms
  // window unmounts this section, and dropping the commit there left the draft
  // holding the new value while Firestore kept the old one — so the curve came
  // back permanently dimmed as `stale`, with nothing on screen saying why. The
  // flush is the identical write the timer was about to make.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.keys(timers).forEach((k) => clearTimeout(timers[k]));
      flushRef.current();
    };
  }, []);

  const selectMode = useCallback((next) => {
    if (next === mode) return;
    const changes = motionModeChanges(next);
    onDraftChange(changes);
    // The existing operation-toggle event, not a new name: a mode change IS the
    // two operations being toggled, and reporting it as `operation_toggled`
    // keeps it in the same funnel as every other switch on this page. The
    // operation named is the one being turned ON.
    trackEvent('operation_toggled', {
      project_id: projectId,
      operation_id: next === MOTION_MODE_PACE ? 'variable_speed' : 'trim_stillness',
      ...changes,
    });
    // Only a change MotionPace is part of needs a settings write: entering pace
    // has to compute a curve, leaving it has to clear one. A trim-only edit
    // keeps persisting at render time exactly as it does today.
    if (next === MOTION_MODE_PACE || mode === MOTION_MODE_PACE) {
      onMotionCommit(changes);
    }
  }, [mode, onDraftChange, onMotionCommit, projectId]);

  const changePaceSetting = useCallback((key, raw) => {
    const value = key === 'maxSpeed' ? resolveMaxSpeed(raw) : resolveSmoothness(raw);
    // Applied to the draft IMMEDIATELY, so the ceiling line tracks the thumb
    // rather than the round trip. Only the commit below is debounced.
    onDraftChange({ [key]: value });

    const timers = timersRef.current;
    const pending = pendingRef.current;
    if (timers[key]) clearTimeout(timers[key]);
    // Replaces any value still pending for this key, so a drag that crosses
    // several values commits once, at the value it settled on.
    pending[key] = value;
    timers[key] = setTimeout(() => {
      delete timers[key];
      delete pending[key];
      reportPaceSetting(key, value);
      onMotionCommit({ [key]: value });
    }, PACE_COMMIT_DEBOUNCE_MS);
  }, [onDraftChange, onMotionCommit, reportPaceSetting]);

  // NOT DEBOUNCED AND NOT COMMITTED, unlike the two sliders above.
  //
  // `showSpeedDial` is not an input to the speed curve — it decides whether the
  // curve is DRAWN on the footage, not how the footage is paced — so it needs
  // no server round trip to take effect. The preview redraws from the draft
  // immediately, and the value reaches Firestore through the same path every
  // other render-time switch uses (`buildRenderSettings`, via
  // `motionPaceSettings`). Routing it through `onMotionCommit` would spend a
  // `recalculate_motion_segments` call — and a whole curve recomputation — on a
  // flag the computation ignores.
  const toggleSpeedDial = useCallback((next) => {
    onDraftChange({ showSpeedDial: next });
    // The same `setting_adjusted` event and the same field names the sliders
    // report, so the dial sits in one funnel with the rest of the edit controls
    // rather than needing its own. No debounce means `from_value` is simply the
    // value being replaced.
    trackEvent('setting_adjusted', {
      control: 'showSpeedDial',
      from_value: !next,
      to_value: next,
      project_id: projectId,
    });
  }, [onDraftChange, projectId]);

  const speedCurve = video ? video[SPEED_CURVE_FIELD] : null;
  const stale = !!speedCurve
    && !speedCurveMatchesSettings(speedCurve, draft.maxSpeed, draft.smoothness);

  return (
    <div className="op-card__body op-motion" data-testid="op-body-motion">
      <div
        className="motion-mode-switch"
        role="radiogroup"
        aria-label={MOTION_SECTION.modeGroupLabel}
        data-testid="motion-mode-switch"
      >
        {/* MOTIONPACE FIRST. The left cell is the one a reader lands on and the
            one arrow-key navigation starts from, and MotionPace is now the mode
            a fresh project opens on (see `seedEditDraft` in lib/editDraft.js),
            so the default sits where the eye goes rather than one cell to the
            right of it. DOM order is the only ordering here — the grid in
            OperationCards.css places cells in source order and has no
            nth-child rules — so this swap moves both the pixels and the tab
            order together. */}
        <ModeOption
          mode={MOTION_MODES.pace}
          selected={mode === MOTION_MODE_PACE}
          onSelect={selectMode}
        />
        <ModeOption
          mode={MOTION_MODES.trim}
          selected={mode === MOTION_MODE_TRIM}
          onSelect={selectMode}
        />
      </div>

      {mode === MOTION_MODE_PACE ? (
        <div className="motion-pace" data-testid="motion-pace-panel">
          <MotionPaceChart
            speedCurve={speedCurve}
            motionValues={video?.motionValues}
            captureGapFrames={video?.captureGapFrames}
            maxSpeed={draft.maxSpeed}
            isRecalculating={isRecalculating}
            stale={stale}
            videoRef={videoRef}
            videoUrl={videoUrl}
          />
          <div className="motion-pace__controls">
            <PaceSlider
              id="motion-pace-max-speed"
              label={MOTION_PACE_CONTROLS.maxSpeed.label}
              hint={MOTION_PACE_CONTROLS.maxSpeed.hint}
              caption={MOTION_PACE_CONTROLS.maxSpeed.caption}
              minLabel={MOTION_PACE_CONTROLS.maxSpeed.minLabel}
              maxLabel={MOTION_PACE_CONTROLS.maxSpeed.maxLabel}
              min={MAX_SPEED_MIN}
              max={MAX_SPEED_MAX}
              step={MAX_SPEED_STEP}
              value={draft.maxSpeed}
              display={`${formatSpeed(draft.maxSpeed)}x`}
              onChange={(e) => changePaceSetting('maxSpeed', e.target.value)}
            />
            <PaceSlider
              id="motion-pace-smoothness"
              label={MOTION_PACE_CONTROLS.smoothness.label}
              hint={MOTION_PACE_CONTROLS.smoothness.hint}
              caption={MOTION_PACE_CONTROLS.smoothness.caption}
              minLabel={MOTION_PACE_CONTROLS.smoothness.minLabel}
              maxLabel={MOTION_PACE_CONTROLS.smoothness.maxLabel}
              min={SMOOTHNESS_MIN}
              max={SMOOTHNESS_MAX}
              step={SMOOTHNESS_STEP}
              value={draft.smoothness}
              // The EFFECTIVE ramp, not the stored setting (#584). The engine
              // applies `rampSeconds = 1.5 * smoothness`, so labelling the raw
              // value with an "s" understated the ramp the user watches by a
              // third at every slider position — "0.5s" for a 0.75s ramp, "3s"
              // for a 4.5s one. The slider still MOVES in stored units and still
              // stores them; only the readout changed.
              display={formatRampSeconds(draft.smoothness)}
              onChange={(e) => changePaceSetting('smoothness', e.target.value)}
            />
          </div>
          <PaceToggle
            id="motion-pace-speed-dial"
            label={MOTION_PACE_CONTROLS.showSpeedDial.label}
            hint={MOTION_PACE_CONTROLS.showSpeedDial.hint}
            caption={MOTION_PACE_CONTROLS.showSpeedDial.caption}
            checked={!!draft.showSpeedDial}
            onChange={toggleSpeedDial}
          />
          <PaceSummary speedCurve={speedCurve} />
        </div>
      ) : (
        motionControls
      )}
    </div>
  );
};

// The one-line receipt under the pace controls: what MotionPace bought, in the
// three numbers the mode is chosen on. Every figure is read from the PERSISTED
// record's exact summaries — never recomputed from the recovered curve, which
// is an approximation good enough to draw and not good enough to quote.
const PaceSummary = ({ speedCurve }) => {
  if (!speedCurve || typeof speedCurve !== 'object') return null;
  const { outputFrames, outputFps, sourceFrames, sourceFps, speedMean, speedMax } = speedCurve;
  const outSeconds = Number.isFinite(outputFrames) && outputFps > 0
    ? outputFrames / outputFps
    : null;
  const srcSeconds = Number.isFinite(sourceFrames) && sourceFps > 0
    ? sourceFrames / sourceFps
    : null;
  if (outSeconds === null || srcSeconds === null) return null;
  return (
    <p className="motion-pace__result" data-testid="motion-pace-result">
      {MOTION_MODES.pace.name} keeps <strong>every frame</strong>
      {' '}&middot; <strong>{formatTime(outSeconds)}</strong> from {formatTime(srcSeconds)}
      {Number.isFinite(speedMean) && Number.isFinite(speedMax)
        ? ` · average ${formatSpeed(speedMean)}x, peak ${formatSpeed(speedMax)}x`
        : ''}
    </p>
  );
};

export default MotionSection;
